// src/components/BankCardsSection.jsx
import React, { useState, useEffect, useRef } from 'react';
import { CreditCard, Shield, Zap, Globe, Star, ChevronRight } from 'lucide-react';

const BankCardsSection = ({ language, t }) => {
  const isRTL = language === 'ar';
  const sectionRef = useRef();
  const [scrollDirection, setScrollDirection] = useState('down');
  const [textProgress, setTextProgress] = useState(0);
  const [cardProgress, setCardProgress] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Get animated text content from translations
  const textContent = t.bankCards.title.words.map((word, index) => ({
    text: word,
    delay: index * 150,
    threshold: 0.1 * index, // Each word appears at 10% intervals
    direction: index % 2 === 0 ? 'left' : 'right' // Alternate sides
  }));

  // Get bank card data from translations
  const bankCards = t.bankCards.cards.map((card, index) => ({
    id: index + 1,
    name: card.name,
    type: card.type,
    gradient: [
      'from-[#2beaf4] via-[#582f8f] to-[#1b0976]',
      'from-[#1b0976] via-[#582f8f] to-[#2beaf4]',
      'from-[#582f8f] via-[#2beaf4] to-[#1b0976]'
    ][index],
    number: [
      '**** **** **** 2847',
      '**** **** **** 8394', 
      '**** **** **** 5729'
    ][index],
    holder: card.holder,
    expiry: ['12/28', '09/27', '06/29'][index],
    features: card.features,
    delay: index * 200,
    // Modern entrance directions
    entranceDirection: ['bottom-left', 'top-right', 'bottom-right'][index]
  }));

  // Scroll detection and animation logic
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const direction = currentScrollY > lastScrollY ? 'down' : 'up';
      setScrollDirection(direction);
      setLastScrollY(currentScrollY);

      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const sectionTop = rect.top;
        const sectionBottom = rect.bottom;
        
        let textProgressValue = 0;
        let cardProgressValue = 0;
        
        // Check if section is in viewport
        if (sectionTop < windowHeight && sectionBottom > 0) {
          // Text animation: starts when section enters viewport
          if (sectionTop <= windowHeight * 0.9) {
            textProgressValue = Math.max(0, Math.min(1, (windowHeight * 0.9 - sectionTop) / (windowHeight * 0.7)));
          }
          
          // Card animation: starts when section is more visible
          if (sectionTop <= windowHeight * 0.7) {
            cardProgressValue = Math.max(0, Math.min(1, (windowHeight * 0.7 - sectionTop) / (windowHeight * 0.6)));
          }
        }
        
        setTextProgress(textProgressValue);
        setCardProgress(cardProgressValue);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-br from-gray-50 via-[#2beaf4]/5 to-[#582f8f]/5 relative overflow-hidden"
    >
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-64 h-64 bg-[#2beaf4]/20 rounded-full filter blur-3xl animate-pulse" />
        <div 
          className="absolute bottom-10 right-10 w-80 h-80 bg-[#582f8f]/20 rounded-full filter blur-3xl animate-pulse" 
          style={{ animationDelay: '2s' }} 
        />
        {/* Additional animated particles */}
        <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-[#1b0976]/15 rounded-full filter blur-2xl animate-bounce" 
             style={{ animationDelay: '1s', animationDuration: '3s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#2beaf4]/15 rounded-full filter blur-2xl animate-pulse" 
             style={{ animationDelay: '0.5s', animationDuration: '4s' }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Modern Animated Text Header */}
        <div className="text-center mb-16 relative overflow-hidden">
          <div className="mb-8 relative">
            {textContent.map((item, index) => {
              const slideDistance = 120; // Distance to slide from
              const slideX = item.direction === 'left' ? -slideDistance : slideDistance;
              const isVisible = textProgress > item.threshold;
              
              return (
                <span
                  key={index}
                  className="inline-block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold relative"
                  style={{
                    transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    transitionDelay: isVisible ? `${item.delay}ms` : '0ms',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible 
                      ? 'translateX(0px) translateY(0px) rotate(0deg) scale(1)' 
                      : `translateX(${slideX}px) translateY(20px) rotate(${item.direction === 'left' ? -5 : 5}deg) scale(0.9)`,
                    background: index % 2 === 0 
                      ? 'linear-gradient(135deg, #2beaf4, #582f8f)' 
                      : 'linear-gradient(135deg, #582f8f, #1b0976)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    marginRight: index < textContent.length - 1 ? (isRTL ? '0' : '0.75rem') : '0',
                    marginLeft: index < textContent.length - 1 ? (isRTL ? '0.75rem' : '0') : '0'
                  }}
                >
                  {item.text}
                  {/* Text shadow effect */}
                  <span 
                    className="absolute inset-0 -z-10"
                    style={{
                      background: index % 2 === 0 
                        ? 'linear-gradient(135deg, #2beaf4, #582f8f)' 
                        : 'linear-gradient(135deg, #582f8f, #1b0976)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      filter: 'blur(8px)',
                      opacity: 0.3,
                      transform: 'translateY(4px)'
                    }}
                  >
                    {item.text}
                  </span>
                </span>
              );
            })}
          </div>
          
          {/* Enhanced Subtitle with slide-up animation */}
          <div className="relative overflow-hidden">
            <p 
              className="text-xl lg:text-2xl text-[#1b0976]/70 max-w-3xl mx-auto relative"
              style={{
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: textProgress > 0.7 ? '800ms' : '0ms',
                opacity: textProgress > 0.7 ? 1 : 0,
                transform: textProgress > 0.7 ? 'translateY(0px)' : 'translateY(50px)'
              }}
            >
              {t.bankCards.subtitle}
              {/* Underline animation */}
              <span 
                className="absolute bottom-0 left-1/2 h-0.5 bg-gradient-to-r from-[#2beaf4] to-[#582f8f]"
                style={{
                  transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transitionDelay: textProgress > 0.8 ? '1200ms' : '0ms',
                  width: textProgress > 0.8 ? '60%' : '0%',
                  transform: 'translateX(-50%)'
                }}
              />
            </p>
          </div>
        </div>

        {/* Enhanced Cards Grid with Modern Animations */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {bankCards.map((card, index) => {
            // Calculate individual card progress with enhanced timing
            const cardStartDelay = index * 0.15;
            const individualCardProgress = Math.max(0, Math.min(1, (cardProgress - cardStartDelay) / (1 - cardStartDelay)));
            
            // Modern entrance animations based on direction
            const getEntranceTransform = (direction, progress) => {
              const distance = 150;
              const easeProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out
              
              switch (direction) {
                case 'bottom-left':
                  return {
                    x: (1 - easeProgress) * -distance,
                    y: (1 - easeProgress) * distance,
                    rotate: (1 - easeProgress) * -15,
                    scale: 0.7 + (0.3 * easeProgress)
                  };
                case 'top-right':
                  return {
                    x: (1 - easeProgress) * distance,
                    y: (1 - easeProgress) * -distance,
                    rotate: (1 - easeProgress) * 15,
                    scale: 0.7 + (0.3 * easeProgress)
                  };
                case 'bottom-right':
                  return {
                    x: (1 - easeProgress) * distance,
                    y: (1 - easeProgress) * distance,
                    rotate: (1 - easeProgress) * -10,
                    scale: 0.7 + (0.3 * easeProgress)
                  };
                default:
                  return { x: 0, y: 0, rotate: 0, scale: 1 };
              }
            };

            const transform = getEntranceTransform(card.entranceDirection, individualCardProgress);
            const isHovered = hoveredCard === card.id;
            
            return (
              <div
                key={card.id}
                className="group relative"
                style={{
                  opacity: individualCardProgress,
                  transform: `translateX(${transform.x}px) translateY(${transform.y}px) rotate(${transform.rotate}deg) scale(${transform.scale})`,
                  transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transitionDelay: `${card.delay}ms`
                }}
                onMouseEnter={() => setHoveredCard(card.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Enhanced 3D Card Container */}
                <div className="relative transform-gpu perspective-1000">
                  
                  {/* Dynamic Glow with Pulse Effect */}
                  <div 
                    className={`absolute inset-0 bg-gradient-to-br ${card.gradient} rounded-2xl filter blur-xl transition-all duration-700`}
                    style={{
                      opacity: individualCardProgress > 0 ? (isHovered ? 0.5 : 0.25) : 0,
                      transform: `scale(${isHovered ? 1.2 : 1.1}) translateY(${isHovered ? 8 : 12}px)`,
                      animation: isHovered ? 'pulse 2s infinite' : 'none'
                    }}
                  />
                  
                  {/* Main Card with Advanced 3D Effects */}
                  <div 
                    className={`relative w-full h-56 sm:h-64 bg-gradient-to-br ${card.gradient} rounded-2xl shadow-2xl overflow-hidden cursor-pointer`}
                    style={{
                      transform: `
                        rotateX(${isHovered ? -8 : 0}deg) 
                        rotateY(${isHovered ? 8 : 0}deg) 
                        translateZ(${isHovered ? 20 : 0}px)
                        scale(${isHovered ? 1.05 : 1})
                      `,
                      transformStyle: 'preserve-3d',
                      transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      boxShadow: isHovered 
                        ? '0 35px 60px -12px rgba(0, 0, 0, 0.3), 0 15px 25px -5px rgba(0, 0, 0, 0.15)' 
                        : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}
                  >
                    
                    {/* Animated Background Patterns */}
                    <div className="absolute inset-0 opacity-10">
                      {/* Rotating circles */}
                      <div 
                        className="absolute top-4 right-4 w-32 h-32 border border-white/20 rounded-full"
                        style={{
                          transform: `rotate(${isHovered ? 180 : 0}deg) scale(${isHovered ? 1.2 : 1})`,
                          transition: 'all 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                        }}
                      />
                      <div 
                        className="absolute bottom-4 left-4 w-24 h-24 border border-white/20 rounded-full"
                        style={{
                          transform: `rotate(${isHovered ? -180 : 0}deg) scale(${isHovered ? 1.3 : 1})`,
                          transition: 'all 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          transitionDelay: '100ms'
                        }}
                      />
                      {/* Pulsing center circle */}
                      <div 
                        className="absolute top-1/2 left-1/2 w-40 h-40 border border-white/10 rounded-full"
                        style={{
                          transform: `translate(-50%, -50%) rotate(${isHovered ? 90 : 0}deg) scale(${isHovered ? 0.8 : 1})`,
                          transition: 'all 1.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          transitionDelay: '200ms'
                        }}
                      />
                    </div>

                    {/* Multiple Animated Light Effects */}
                    <div 
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -skew-x-12"
                      style={{
                        transform: `translateX(${isHovered ? '200%' : '-200%'}) skewX(-12deg)`,
                        transition: 'transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        width: '50%'
                      }}
                    />
                    
                    {/* Secondary shimmer */}
                    <div 
                      className="absolute inset-0 bg-gradient-to-l from-transparent via-white/15 to-transparent skew-x-12"
                      style={{
                        transform: `translateX(${isHovered ? '-200%' : '200%'}) skewX(12deg)`,
                        transition: 'transform 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        transitionDelay: '200ms',
                        width: '60%'
                      }}
                    />

                    {/* Enhanced Card Content */}
                    <div className="relative h-full p-6 flex flex-col justify-between text-white">
                      
                      {/* Card Header with Micro-animations */}
                      <div className="flex justify-between items-start">
                        <div 
                          style={{
                            transform: `translateX(${isHovered ? 4 : 0}px) translateY(${isHovered ? -2 : 0}px)`,
                            transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                          }}
                        >
                          <h3 className="text-lg font-bold">{card.name}</h3>
                          <p className="text-sm opacity-90">{card.type}</p>
                        </div>
                        <div 
                          className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm"
                          style={{
                            transform: `rotate(${isHovered ? 15 : 0}deg) scale(${isHovered ? 1.15 : 1})`,
                            transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                          }}
                        >
                          <CreditCard className="w-5 h-5" />
                        </div>
                      </div>

                      {/* Card Number with Enhanced Animation */}
                      <div className="my-4">
                        <p 
                          className="text-lg font-mono tracking-wider"
                          style={{
                            transform: `scale(${isHovered ? 1.05 : 1}) translateX(${isHovered ? 2 : 0}px)`,
                            transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                            textShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.3)' : 'none'
                          }}
                        >
                          {card.number}
                        </p>
                      </div>

                      {/* Card Footer with Smooth Animation */}
                      <div 
                        className="flex justify-between items-end"
                        style={{
                          transform: `translateY(${isHovered ? -3 : 0}px)`,
                          transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                        }}
                      >
                        <div>
                          <p className="text-xs opacity-75">CARD HOLDER</p>
                          <p className="text-sm font-semibold">{card.holder}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs opacity-75">EXPIRES</p>
                          <p className="text-sm font-semibold">{card.expiry}</p>
                        </div>
                      </div>

                      {/* Enhanced Chip with 3D Effect */}
                      <div 
                        className="absolute top-20 left-6 w-10 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded"
                        style={{
                          transform: `scale(${isHovered ? 1.1 : 1}) rotateY(${isHovered ? 15 : 0}deg) rotateX(${isHovered ? -10 : 0}deg)`,
                          transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          transformStyle: 'preserve-3d',
                          opacity: isHovered ? 1 : 0.8,
                          boxShadow: isHovered ? '0 4px 8px rgba(0,0,0,0.3)' : 'none'
                        }}
                      />
                      
                      {/* Enhanced Logo with Pulse Animation */}
                      <div className="absolute top-6 right-6">
                        <div 
                          className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm"
                          style={{
                            transform: `scale(${isHovered ? 1.25 : 1}) rotateZ(${isHovered ? 180 : 0}deg)`,
                            transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                            animation: isHovered ? 'pulse 1.5s infinite' : 'none'
                          }}
                        >
                          <span className="text-xs font-bold">M</span>
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Multi-layer Hover Overlay */}
                    <div 
                      className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/5 to-white/10"
                      style={{
                        opacity: isHovered ? 1 : 0,
                        transition: 'opacity 0.5s ease-out'
                      }}
                    />
                    
                    {/* Additional shimmer layers */}
                    <div 
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-200/20 to-transparent"
                      style={{
                        transform: `translateX(${isHovered ? '150%' : '-150%'}) rotate(15deg)`,
                        transition: 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        transitionDelay: '100ms',
                        opacity: isHovered ? 1 : 0
                      }}
                    />
                  </div>
                </div>

                {/* Enhanced Card Features with Slide-in Animation */}
                <div 
                  className="mt-6 space-y-3"
                  style={{
                    opacity: individualCardProgress > 0.7 ? 1 : 0,
                    transform: `translateY(${individualCardProgress > 0.7 ? 0 : 30}px) translateX(${individualCardProgress > 0.7 ? 0 : (index % 2 === 0 ? -20 : 20)}px)`,
                    transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    transitionDelay: `${card.delay + 400}ms`
                  }}
                >
                  <h4 className="text-lg font-semibold text-[#1b0976] mb-3">{card.features.title}</h4>
                  {card.features.items.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex}
                      className={`flex items-center text-[#1b0976]/70 ${
                        isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'
                      }`}
                      style={{
                        transform: `translateX(${isHovered ? 6 : 0}px)`,
                        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        transitionDelay: `${featureIndex * 50}ms`
                      }}
                    >
                      <div 
                        className="w-2 h-2 bg-gradient-to-r from-[#2beaf4] to-[#582f8f] rounded-full"
                        style={{
                          transform: `scale(${isHovered ? 1.5 : 1})`,
                          transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          boxShadow: isHovered ? '0 0 8px rgba(42, 234, 244, 0.5)' : 'none'
                        }}
                      />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                  
                  {/* Ultra-enhanced Learn More Button */}
                  <button 
                    className={`mt-4 inline-flex items-center text-[#582f8f] hover:text-[#1b0976] font-medium group relative overflow-hidden px-4 py-2 rounded-lg ${
                      isHovered ? 'bg-[#2beaf4]/10' : ''
                    }`}
                    style={{
                      transform: `translateX(${isHovered ? 8 : 0}px)`,
                      transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                    }}
                  >
                    <span className="relative z-10">{t.bankCards.cta.learnMore}</span>
                    <ChevronRight 
                      className={`w-4 h-4 ${isRTL ? 'mr-1' : 'ml-1'}`}
                      style={{
                        transform: `translateX(${isHovered ? 4 : 0}px) rotate(${isHovered ? 15 : 0}deg)`,
                        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                      }}
                    />
                    
                    {/* Animated background */}
                    <div 
                      className="absolute inset-0 bg-gradient-to-r from-[#2beaf4]/10 to-[#582f8f]/10 rounded-lg"
                      style={{
                        transform: `scaleX(${isHovered ? 1 : 0})`,
                        transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        transformOrigin: isRTL ? 'right' : 'left'
                      }}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Enhanced Bottom CTA */}
        <div 
          className="text-center mt-16"
          style={{
            opacity: cardProgress > 0.8 ? 1 : 0,
            transform: `translateY(${cardProgress > 0.8 ? 0 : 40}px) scale(${cardProgress > 0.8 ? 1 : 0.9})`,
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: cardProgress > 0.8 ? '600ms' : '0ms'
          }}
        >
          <button className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#2beaf4] to-[#582f8f] text-white rounded-xl hover:from-[#582f8f] hover:to-[#1b0976] transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105">
            {t.bankCards.cta.button}
            <ChevronRight className={`w-5 h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default BankCardsSection;